import { ANIMATION, BORDERS, COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, type DimensionValue, Platform, View, type ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import { Constants, useScreenSize } from '../../utils';
import { Column } from '../containers';
import { Illustration } from '../images';
import { INTERNAL_ActionSheetPropsContext } from './ActionSheetProvider';
import { BottomSheet, type BottomSheetProps } from './BottomSheet';
import { type ActionSheetProps } from './DEPRECATED_ActionSheet';

const isTest = !!process.env.JEST_WORKER_ID;

export type ActionSheetV2Props = Partial<Omit<BottomSheetProps, 'alwaysScrollable' | 'hasScrollableContents'>> &
  Pick<ActionSheetProps, 'illustrationSrc' | 'illustrationXl'> & {
    readonly illustrationAccessibilityLabel?: string;

    /**
     * decrease the modal width
     */
    readonly narrow?: boolean;

    /**
     * increase the viewport height
     */
    readonly fullscreen?: boolean;

    /**
     * Callback for any side-effects
     */
    readonly onBack?: () => void;

    /**
     * Type of action sheet to send as part of analytics, takes precedence over title
     */
    readonly analyticsName?: string;

    /**
     * Native only. When true, adds bottom margin equal to keyboard height so the sheet stays above
     * the keyboard. Use when `react-native-modal` avoidKeyboard does not move the sheet (e.g. iOS with
     * `coverScreen={false}` on the provider).
     */
    readonly keyboardAvoiding?: boolean;
  };

/**
 * !⚠️ Don't set `overflow: hidden` on the container or the illustration will be cut off.
 */
const defaultStyles: ViewStyle = {
  backgroundColor: COLORS.BACKGROUND_COLORS.WHITE,
  flexShrink: 1,
};

const largeScreenStyles: ViewStyle = {
  ...defaultStyles,
  transform: 'translate(-50%, -50%)',
  left: '50%',
  top: '50%',
  position: 'fixed',
  width: '600px',
  borderRadius: BORDERS.RADIUS.XL.px,
  margin: 'auto',
} as any; // this should be done within a styled component, not as a JS object

const smallScreenStyles: ViewStyle = {
  ...defaultStyles,
  borderTopLeftRadius: BORDERS.RADIUS.XL.value,
  borderTopRightRadius: BORDERS.RADIUS.XL.value,
};

const getHeightProp = (fullscreen?: boolean): Record<never, never> | Record<'height', DimensionValue> => {
  if (!fullscreen) {
    return {};
  }

  if (Platform.OS !== 'web') {
    return { height: '100%' };
  }

  return { height: '90vh' };
};

const platformStyle = (
  isMDScreenOrBigger: boolean,
  fullscreen?: boolean,
  narrow?: boolean,
  compact?: boolean,
): ViewStyle => {
  const heightProp = getHeightProp(fullscreen);
  const flexGrow = compact ? 0 : 1;
  if (Platform.OS !== 'web') {
    return { ...smallScreenStyles, ...heightProp, flexGrow };
  }

  const maxHeight = '90vh' as DimensionValue;
  if (isMDScreenOrBigger) {
    const narrowStyles = narrow ? { width: '500px' as DimensionValue } : {};
    return { ...largeScreenStyles, ...heightProp, maxHeight, ...narrowStyles, flexGrow };
  }

  return { ...smallScreenStyles, ...heightProp, maxHeight, flexGrow };
};

const IllustrationAnchor = styled(View)<{ illustrationXl?: boolean }>`
  position: absolute;
  right: ${SPACING.STATIC.LG.px};
  top: -${({ illustrationXl }) => (illustrationXl ? SIZES.ILLUSTRATION.XL.value : SIZES.ILLUSTRATION.LG.value) / 2}px;
  z-index: 1;
`;

const StyledAnimatedView = styled(Animated.View)<{
  isMDScreenOrBigger: boolean;
  fullscreen?: boolean;
  /** When true, the sheet does not grow to fill space (e.g. compact content); avoids white space below buttons. */
  compact?: boolean;
}>`
  flex-shrink: 1;
  flex-grow: ${({ compact }) => (compact ? 0 : 1)};
  ${({ isMDScreenOrBigger }) => (isMDScreenOrBigger ? `padding-bottom: ${SPACING.STATIC.LG.px};` : '')}
`;

/**
 * Used to render an action sheet. As a layout component, it has several props that render content
 * in different places. The order is the following:
 *
 * ```
 * ┌────────────────────────────────────────illustrationSrc──┐
 * │                                                         │
 * │  Tag (optional)                                         │
 * │  title (large serif text)                               │
 * │  description (optional, small text)                     │
 * │  children (arbitrary content)                           │
 * │  buttons (optional)                                     │
 * │  footnote (optional arbitrary content below buttons)    │
 * │                                                         │
 * └─────────────────────────────────────────────────────────┘
 * ```
 */
export const ActionSheetV2 = ({
  handleBack: handleBackOverride,
  handleClose: handleCloseOverride,
  onBack,
  illustrationAccessibilityLabel,
  illustrationSrc,
  illustrationXl,
  buttons,
  checkbox,
  contentContainerStyle,
  description,
  footnote,
  Tag,
  title,
  children,
  fullscreen,
  narrow,
  analyticsName,
  dismissible,
  keyboardAvoiding = false,
}: ActionSheetV2Props) => {
  const { handleBack, handleClose, createAnalyticsEvent, isFirstSheet } = React.useContext(
    INTERNAL_ActionSheetPropsContext,
  );
  const { isMDScreenOrBigger } = useScreenSize();
  const keyboardHeight = useKeyboardHeight();
  const fadeAnimationValue = useRef(new Animated.Value(1)).current;

  // animate the fade in and out when the action sheet is mounted and unmounted
  useEffect(() => {
    if (isTest) return; // skip animations in tests

    Animated.timing(fadeAnimationValue, {
      toValue: 1,
      duration: ANIMATION.SPEEDS.FAST,
      useNativeDriver: Constants.useNativeDriver,
    }).start();

    return () => {
      if (isTest) return;
      Animated.timing(fadeAnimationValue, {
        toValue: 0,
        duration: ANIMATION.SPEEDS.FAST,
        useNativeDriver: Constants.useNativeDriver,
      }).start();
    };
  }, [fadeAnimationValue]);

  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    createAnalyticsEvent(analyticsName || title || 'Unknown');
  }, [createAnalyticsEvent, analyticsName, title]);

  const handleBackWrapper = useCallback(() => {
    // the handle back override use case is mainly for navigating to a totally different screen
    if (handleBackOverride) {
      handleBackOverride();

      // we also need to close the global action sheet or else it will be in mixed state.
      handleClose();
    } else {
      handleBack?.();
    }

    onBack?.();
  }, [handleBackOverride, onBack, handleClose, handleBack]);

  const handleCloseWrapper = useCallback(() => {
    handleClose();

    if (handleCloseOverride) {
      handleCloseOverride();
    }
  }, [handleClose, handleCloseOverride]);

  const handleEscapeKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e?.key === 'Escape') {
        handleCloseWrapper();
      }
    },
    [handleCloseWrapper],
  );

  const compact = !!contentContainerStyle;
  const columnStyle = useMemo((): ViewStyle => {
    const base = platformStyle(isMDScreenOrBigger, fullscreen && !compact, narrow, compact);
    if (!keyboardAvoiding || Platform.OS === 'web') {
      return base;
    }
    return { ...base, marginBottom: keyboardHeight };
  }, [compact, fullscreen, isMDScreenOrBigger, keyboardAvoiding, keyboardHeight, narrow]);

  return (
    <Column
      accessible={false}
      style={columnStyle}
    >
      {!!illustrationSrc && (
        <IllustrationAnchor illustrationXl={illustrationXl}>
          <Illustration
            size={illustrationXl ? 'xl' : 'lg'}
            source={illustrationSrc}
            accessibilityLabel={illustrationAccessibilityLabel}
          />
        </IllustrationAnchor>
      )}
      <StyledAnimatedView
        accessible={false}
        // eslint-disable-next-line react-native/no-inline-styles
        style={{ opacity: isTest || Platform.OS === 'web' ? 1 : fadeAnimationValue }}
        compact={compact}
        fullscreen={fullscreen}
        isMDScreenOrBigger={isMDScreenOrBigger}
        // @ts-expect-error this does exist on web, couldn't get global TS override to work with animated component
        onKeyUp={Platform.OS === 'web' ? handleEscapeKeyPress : undefined}
      >
        <BottomSheet
          alwaysScrollable
          buttons={buttons}
          checkbox={checkbox}
          contentContainerStyle={contentContainerStyle}
          description={description}
          dismissible={dismissible}
          footnote={footnote}
          handleBack={handleBackOverride || handleBack ? handleBackWrapper : undefined}
          handleClose={handleCloseWrapper}
          isFirstSheet={!!isFirstSheet && !handleBack}
          Tag={Tag}
          title={title}
        >
          {children}
        </BottomSheet>
      </StyledAnimatedView>
    </Column>
  );
};
